import { Link } from '@tanstack/react-router';
import { ArrowLeft } from '@phosphor-icons/react';
import { cx } from '../../lib/cx';
import { Icon } from '../Icon';
import { useToast } from '../ui/toast';

const TAB =
  'flex flex-col gap-(--mbm-space-2-5) focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)';

type TabId = 'overview' | 'projects' | 'deliverables';

/** The tabs of Figma A03.2 that have no page yet: drawn as designed, and they answer "coming soon". */
const INERT_TABS = ['Billing', 'Bookings', 'Chat', 'Audits', 'Activity'] as const;

function Label({ text, count, active }: { text: string; count?: number; active: boolean }) {
  return (
    <>
      <span className="flex items-center gap-(--mbm-space-1-5) pt-(--mbm-space-1)">
        <span className={cx('text-label-medium', active ? 'text-text-primary' : 'text-text-secondary')}>{text}</span>
        {count !== undefined ? <span className="text-body-xsmall text-text-tertiary">{count}</span> : null}
      </span>
      <span aria-hidden className={cx('h-(--mbm-space-0-5) w-full', active ? 'bg-action-primary' : 'bg-transparent')} />
    </>
  );
}

/** "← Businesses / Name": the first line of every business page (Figma A03.2, A05.1). */
export function BusinessBreadcrumb({ businessName }: { businessName: string }) {
  return (
    <nav
      aria-label="Breadcrumb"
      className="flex items-center gap-(--mbm-space-1-5) text-label-medium text-text-secondary"
    >
      <Link
        to="/subdomain/admin/businesses"
        search={{ tab: undefined, q: undefined, am: undefined }}
        className="inline-flex items-center gap-(--mbm-space-1-5) hover:text-text-primary focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)"
      >
        <Icon icon={ArrowLeft} size="sm" />
        Businesses
      </Link>
      <span aria-hidden className="font-normal text-text-tertiary">
        /
      </span>
      <span className="text-text-primary">{businessName}</span>
    </nav>
  );
}

/**
 * The tab row under a business's header (Figma A03.2, A05.1).
 * TODO: Billing, Bookings, Chat, Audits and Activity arrive with their sprints (billing S4, bookings S3, chat S5);
 * until then they answer "coming soon" rather than being left out. Their counts need those sprints too.
 */
export function BusinessTabs({
  businessId,
  active,
  projectCount,
  deliverableCount,
}: {
  businessId: string;
  active: TabId;
  projectCount?: number;
  deliverableCount?: number;
}) {
  const toast = useToast();
  return (
    <div
      className="flex gap-(--mbm-space-6) border border-border-line"
      role="navigation"
      aria-label="Business sections"
    >
      <Link
        to="/subdomain/admin/businesses/$businessId"
        params={{ businessId }}
        aria-current={active === 'overview' ? 'page' : undefined}
        className={TAB}
      >
        <Label text="Overview" active={active === 'overview'} />
      </Link>
      <Link
        to="/subdomain/admin/businesses/$businessId/projects"
        params={{ businessId }}
        search={{ bucket: 'active', domain: undefined, q: undefined }}
        aria-current={active === 'projects' ? 'page' : undefined}
        className={TAB}
      >
        <Label text="Projects & tasks" count={projectCount} active={active === 'projects'} />
      </Link>
      <Link
        to="/subdomain/admin/businesses/$businessId/deliverables"
        params={{ businessId }}
        aria-current={active === 'deliverables' ? 'page' : undefined}
        className={TAB}
      >
        <Label text="Deliverables" count={deliverableCount} active={active === 'deliverables'} />
      </Link>
      {INERT_TABS.map((tab) => (
        <button
          key={tab}
          type="button"
          aria-disabled
          onClick={() =>
            toast({
              title: 'Coming soon',
              description: `${tab} for a business arrives with its own sprint.`,
              tone: 'info',
            })
          }
          className={cx(TAB, 'cursor-not-allowed')}
        >
          <Label text={tab} active={false} />
        </button>
      ))}
    </div>
  );
}
